/**
 * Modèle Personnage COF2.
 *
 * Source de vérité unique pour un PJ. Validé par Zod, immutable en lecture
 * (on retourne de nouveaux objets à chaque mutation - pas de `readonly` stricts
 * dans le schema pour permettre la (dé)sérialisation JSON).
 *
 * Les calculs dérivés (PV, DEF, init, PM, PC, DR, attaques) ne sont PAS stockés
 * ici - ils sont recalculés à la volée par `derived.ts` pour éviter la divergence.
 * Le personnage stocke uniquement les valeurs de base et l'état live
 * (PV actuels, buffs, états, inventaire, notes).
 */

import { z } from 'zod';
import { CARACS, CARAC_MAX, CARAC_MIN, type Caracs } from './caracs';
import { FAMILLES } from './famille';

export const SCHEMA_VERSION = 2 as const;

const CaracSchema = z.number().int().min(CARAC_MIN).max(CARAC_MAX);

const CaracsSchema = z.object({
  FOR: CaracSchema,
  AGI: CaracSchema,
  CON: CaracSchema,
  INT: CaracSchema,
  PER: CaracSchema,
  CHA: CaracSchema,
  VOL: CaracSchema,
});

const RangsSchema = z.record(z.string(), z.number().int().min(1).max(8));

const PrestigeEntrySchema = z.object({
  voie_id: z.string().min(1),
  rang: z.number().int().min(4).max(8),
});

const BuffSchema = z.object({
  id: z.string().min(1),
  tours: z.number().int().min(0).nullable(),
});

const EtatSchema = z.object({
  id: z.string().min(1),
  tours: z.number().int().min(0).nullable(),
});

const CompagnonActifSchema = z.object({
  id: z.string().uuid(),
  nom: z.string().min(1),
  compagnonBaseId: z.string().min(1),
  voieSourceId: z.string().min(1),
  pvActuel: z.number().int().nullable().default(null),
  etats: z.array(EtatSchema).default([]),
  buffs: z.array(BuffSchema).default([]),
  notes: z.string().default(''),
});

const InventaireItemSchema = z.object({
  uid: z.string().min(1),
  cat: z.string(),
  nom: z.string().min(1),
  qty: z.number().int().positive(),
});

const EquipementSchema = z.object({
  armure: z.string().nullable().default(null),
  bouclier: z.string().nullable().default(null),
  main_1: z.string().nullable().default(null),
  main_2: z.string().nullable().default(null),
  distance: z.string().nullable().default(null),
});

const BourseSchema = z.object({
  pp: z.number().int().nonnegative().default(0),
  po: z.number().int().nonnegative().default(0),
  pa: z.number().int().nonnegative().default(0),
  pc: z.number().int().nonnegative().default(0),
});

const PeupleChoiceResolutionSchema = z.object({
  optionsChoices: z.array(z.union([z.string(), z.tuple([z.string(), z.string()])])),
  malusChoices: z.array(z.string()),
});

/**
 * Schéma complet du Personnage. Tous les champs à valeurs par défaut utilisent
 * `.default(...)` pour que `PersonnageSchema.parse({...})` accepte des objets
 * minimalistes venant du wizard.
 */
export const PersonnageSchema = z.object({
  id: z.string().uuid(),
  schemaVersion: z.literal(SCHEMA_VERSION),
  createdAt: z.string(),
  updatedAt: z.string(),

  // Identité
  nom: z.string().min(1).max(80),
  concept: z.string().default(''),
  ideal: z.string().default(''),
  defaut: z.string().default(''),
  phobie: z.string().default(''),
  secretIntime: z.string().default(''),
  talentSecondaire: z.string().nullable().default(null),
  notes: z.string().default(''),
  notesPrivees: z.string().default(''),

  // Appartenance
  profilId: z.string().min(1),
  profilSecondaireId: z.string().nullable().default(null),
  peupleId: z.string().min(1),
  peupleChoices: PeupleChoiceResolutionSchema,
  diviniteId: z.string().nullable().default(null),

  // Niveau & carac
  niveau: z.number().int().min(1).max(20),
  caracs: CaracsSchema,
  // Caracs bonus permanents dus à la progression (montée de niveau, boost PC Cap).
  caracsBoosts: CaracsSchema.default({
    FOR: 0,
    AGI: 0,
    CON: 0,
    INT: 0,
    PER: 0,
    CHA: 0,
    VOL: 0,
  }),

  // Voies
  voiesProfil: z.array(z.string()).max(6).default([]),
  voiePeupleId: z.string().nullable().default(null),
  useVoieMage: z.boolean().default(false),
  rangs: RangsSchema.default({}),
  choixVoies: z.record(z.string(), z.string()).default({}),
  voiesPrestige: z.array(PrestigeEntrySchema).max(1).default([]),

  // État live
  pvActuel: z.number().int().nullable().default(null),
  pcActuel: z.number().int().nullable().default(null),
  drActuel: z.number().int().nullable().default(null),
  pmActuel: z.number().int().nullable().default(null),

  // Buffs et états (règles de combat)
  buffs: z.array(BuffSchema).default([]),
  etats: z.array(EtatSchema).default([]),

  // Inventaire / équipement / bourse
  inventaire: z.array(InventaireItemSchema).default([]),
  equipement: EquipementSchema.default({
    armure: null,
    bouclier: null,
    main_1: null,
    main_2: null,
    distance: null,
  }),
  bourse: BourseSchema.default({ pp: 0, po: 0, pa: 0, pc: 0 }),

  // Compagnons actifs (familiers, animaux, golems, montures)
  compagnons: z.array(CompagnonActifSchema).default([]),

  // Langues connues (identifiants libres)
  langues: z.array(z.string()).default([]),

  // Historique de dés (stats par type)
  diceHistory: z.record(z.string(), z.array(z.number().int().positive())).default({}),

  // Lien campagne cloud
  campaignId: z.string().nullable().default(null),
  deviceOrigin: z.string().nullable().default(null),
});

export type Personnage = z.infer<typeof PersonnageSchema>;
export type CompagnonActif = z.infer<typeof CompagnonActifSchema>;

/**
 * Input du wizard de création (tous champs optionnels avec défauts).
 */
export interface PersonnageCreationInput {
  nom: string;
  profilId: string;
  peupleId: string;
  peupleChoices: z.infer<typeof PeupleChoiceResolutionSchema>;
  caracs: Caracs;
  voiesProfil?: readonly string[];
  voiePeupleId?: string | null;
  useVoieMage?: boolean;
  rangs?: Record<string, number>;
  niveau?: number;
  concept?: string;
  ideal?: string;
  defaut?: string;
  phobie?: string;
  deviceOrigin?: string | null;
}

/**
 * Crée un nouveau personnage valide. Lance une Error si les inputs ne passent
 * pas Zod (ex: nom vide, carac hors bornes, niveau invalide).
 */
export function createPersonnage(
  input: PersonnageCreationInput,
  now: () => string = () => new Date().toISOString(),
  uuid: () => string = () => crypto.randomUUID(),
): Personnage {
  const id = uuid();
  const iso = now();
  const raw: unknown = {
    id,
    schemaVersion: SCHEMA_VERSION,
    createdAt: iso,
    updatedAt: iso,
    nom: input.nom,
    profilId: input.profilId,
    peupleId: input.peupleId,
    peupleChoices: input.peupleChoices,
    niveau: input.niveau ?? 1,
    caracs: input.caracs,
    voiesProfil: input.voiesProfil ?? [],
    voiePeupleId: input.voiePeupleId ?? null,
    useVoieMage: input.useVoieMage ?? false,
    rangs: input.rangs ?? {},
    concept: input.concept ?? '',
    ideal: input.ideal ?? '',
    defaut: input.defaut ?? '',
    phobie: input.phobie ?? '',
    deviceOrigin: input.deviceOrigin ?? null,
  };
  return PersonnageSchema.parse(raw);
}

/**
 * Validation d'un personnage complet. Retourne soit le personnage validé,
 * soit la liste des erreurs (chaînes lisibles) sans jeter d'exception.
 */
export function validatePersonnage(
  raw: unknown,
): { ok: true; value: Personnage } | { ok: false; errors: readonly string[] } {
  const r = PersonnageSchema.safeParse(raw);
  if (r.success) return { ok: true, value: r.data };
  return {
    ok: false,
    errors: r.error.issues.map((i) => `${i.path.join('.') || '<racine>'} : ${i.message}`),
  };
}

/**
 * Clamp et normalise un personnage : valeurs live dans les bornes, timestamps
 * à jour, purge d'entrées invalides. Retourne une nouvelle instance.
 */
export function normalizePersonnage(
  p: Personnage,
  now = () => new Date().toISOString(),
): Personnage {
  const updated: Personnage = {
    ...p,
    updatedAt: now(),
    buffs: p.buffs.filter((b) => b.tours === null || b.tours > 0),
    etats: p.etats.filter((e) => e.tours === null || e.tours > 0),
  };
  return updated;
}

/**
 * Vérifie la cohérence haut niveau (champs présents, famille connue, etc.).
 * Appelé après `validatePersonnage` pour des règles croisées non exprimables en Zod.
 */
export function integrityCheck(
  p: Personnage,
  famillesById: Record<string, { famille_id: string }>,
): readonly string[] {
  const errors: string[] = [];

  // La carac principale du profil doit exister (indirect : le profil existe).
  if (!famillesById[p.profilId]) {
    errors.push(`Profil inconnu : "${p.profilId}".`);
  } else {
    const famille = famillesById[p.profilId]?.famille_id;
    if (famille && !(famille in FAMILLES) && famille !== 'generique') {
      errors.push(`Famille "${famille}" inconnue pour le profil "${p.profilId}".`);
    }
  }

  // Voies de prestige : rang entre 4 et 8.
  for (const vp of p.voiesPrestige) {
    if (vp.rang < 4 || vp.rang > 8) {
      errors.push(`Voie prestige "${vp.voie_id}" : rang ${vp.rang} hors 4-8.`);
    }
  }

  // Rangs : chaque voie listée dans `rangs` doit être référencée ailleurs.
  const declared = new Set<string>([
    ...p.voiesProfil,
    ...(p.voiePeupleId ? [p.voiePeupleId] : []),
    ...p.voiesPrestige.map((v) => v.voie_id),
  ]);
  for (const voieId of Object.keys(p.rangs)) {
    if (!declared.has(voieId)) {
      errors.push(`Rang déclaré pour une voie non active : "${voieId}".`);
    }
  }

  // Caracs : bornes.
  for (const k of CARACS) {
    const v = p.caracs[k];
    if (v < CARAC_MIN || v > CARAC_MAX) {
      errors.push(`Carac ${k} = ${v} hors bornes [${CARAC_MIN}, ${CARAC_MAX}].`);
    }
  }

  return errors;
}
